import React, { useState } from 'react'
import { contactAPI } from '../services/api'
import './ContactForm.css'

/**
 * Reusable public form for contacting the club or asking to join.
 *
 * Spam protection: a hidden "honeypot" field (_hp) that real users never see;
 * bots that fill it get a silent success and nothing is stored/emailed. The
 * backend also validates and records every genuine submission and emails the
 * configured recipient.
 *
 * @param {'contact'|'join'} kind
 */
function ContactForm({ kind = 'contact' }) {
  const isJoin = kind === 'join'
  const [form, setForm] = useState({
    name: '', email: '', phone: '', subject: '',
    message: '', experience: '', _hp: '',
  })
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [errors, setErrors] = useState([])

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

  const validate = () => {
    const errs = []
    if (!form.name.trim()) errs.push('Please enter your name.')
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.push('Please enter a valid email address.')
    if (!isJoin && !form.message.trim()) errs.push('Please enter a message.')
    return errs
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    const errs = validate()
    setErrors(errs)
    if (errs.length) return

    setStatus('submitting')
    try {
      await contactAPI.submit(kind, form)
      setStatus('success')
    } catch (err) {
      setErrors([err.message || 'Something went wrong. Please try again.'])
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className="contact-form-success" role="status">
        <h3>Thanks{form.name ? `, ${form.name.split(' ')[0]}` : ''}!</h3>
        <p>
          Your {isJoin ? 'request to join' : 'message'} has reached the club. We'll get back to
          you by email soon. Fly safe.
        </p>
      </div>
    )
  }

  return (
    <form className="contact-form" onSubmit={onSubmit} noValidate>
      {errors.length > 0 && (
        <div className="contact-form-errors" role="alert">
          <ul>{errors.map((er, i) => <li key={i}>{er}</li>)}</ul>
        </div>
      )}

      <div className="form-row">
        <label htmlFor="cf-name">Name</label>
        <input id="cf-name" name="name" type="text" value={form.name} onChange={update} required autoComplete="name" />
      </div>

      <div className="form-row">
        <label htmlFor="cf-email">Email</label>
        <input id="cf-email" name="email" type="email" value={form.email} onChange={update} required autoComplete="email" />
      </div>

      <div className="form-row">
        <label htmlFor="cf-phone">Phone <span className="optional">(optional)</span></label>
        <input id="cf-phone" name="phone" type="tel" value={form.phone} onChange={update} autoComplete="tel" />
      </div>

      {isJoin ? (
        <div className="form-row">
          <label htmlFor="cf-experience">Flying experience <span className="optional">(optional)</span></label>
          <input
            id="cf-experience"
            name="experience"
            type="text"
            value={form.experience}
            onChange={update}
            placeholder="e.g. BHPA CP / Pilot, hours, wing"
          />
        </div>
      ) : (
        <div className="form-row">
          <label htmlFor="cf-subject">Subject <span className="optional">(optional)</span></label>
          <input id="cf-subject" name="subject" type="text" value={form.subject} onChange={update} />
        </div>
      )}

      <div className="form-row">
        <label htmlFor="cf-message">
          {isJoin ? 'Tell us a little about yourself' : 'Message'}
          {!isJoin && ' '}
          {isJoin && <span className="optional">(optional)</span>}
        </label>
        <textarea
          id="cf-message"
          name="message"
          rows={isJoin ? 4 : 6}
          value={form.message}
          onChange={update}
          required={!isJoin}
        />
      </div>

      {/* Honeypot - hidden from users, catches bots. Do not remove. */}
      <div className="cf-hp" aria-hidden="true">
        <label htmlFor="cf-website">Website</label>
        <input id="cf-website" name="_hp" type="text" tabIndex="-1" autoComplete="off" value={form._hp} onChange={update} />
      </div>

      <button type="submit" className="btn btn-primary" disabled={status === 'submitting'}>
        {status === 'submitting' ? 'Sending...' : isJoin ? 'Send join request' : 'Send message'}
      </button>
    </form>
  )
}

export default ContactForm
